import { useEffect, useRef } from 'react';
import type { Message, BubbleOption, ChatAction } from '../types';
import { MESSAGE_TYPES } from '../constants/messageTypes';

interface UseIntroMessageOptions {
  historyLoaded: boolean;
  messagesLength: number;
  welcomeMessage: string;
  filteredBubbleOptions: BubbleOption[];
  activeSessionId: string | null;
  threadId: string | null;
  dispatch: (action: ChatAction) => void;
}

export function useIntroMessage({
  historyLoaded,
  messagesLength,
  welcomeMessage,
  filteredBubbleOptions,
  activeSessionId,
  threadId,
  dispatch,
}: UseIntroMessageOptions): void {
  const introSentRef = useRef<string | null>(null);

  useEffect(() => {
    if (!historyLoaded) return;
    if (!welcomeMessage || !welcomeMessage.trim()) return;
    if (messagesLength > 0) return;

    const sessionKey = activeSessionId || threadId || 'default';
    if (introSentRef.current === sessionKey) return;

    const buttons = Array.isArray(filteredBubbleOptions)
      ? filteredBubbleOptions
          .map(option => {
            if (typeof option === 'string') {
              return { label: option, value: option };
            }
            if (option && typeof option === 'object') {
              return {
                label: option.label,
                value: option.value || option.label,
                href: option.href,
                target: option.target,
              };
            }
            return null;
          })
          .filter((x): x is NonNullable<typeof x> => x != null)
      : [];

    const now = new Date();
    const introMessage: Message = {
      id: `intro-${now.getTime()}`,
      sender: 'ai',
      text: welcomeMessage,
      timestamp: now.toISOString(),
      status: 'delivered',
      type: buttons.length > 0 ? MESSAGE_TYPES.BUTTONS : MESSAGE_TYPES.TEXT,
      richContent: buttons.length > 0 ? { buttons, buttonStyle: 'pill' } : undefined,
    };

    dispatch({ type: 'ADD_MESSAGE', message: introMessage });
    introSentRef.current = sessionKey;
  }, [
    historyLoaded,
    messagesLength,
    welcomeMessage,
    filteredBubbleOptions,
    activeSessionId,
    threadId,
    dispatch,
  ]);
}
